import { makeObservable, observable, action } from "mobx";

// Models & Services
import { ContainerModel } from "models/locker/entities/ContainerModel";

import { ToastHelper } from "helpers/ToastHelper";
import LocalAPI from "services/LocalAPI";

/**
 * Store para gerenciar a comunicação com a API local e o estado das portas/compartimentos.
 */
class DoorsStore {
    // --- Estado Observável ---
    container?: ContainerModel;

    // --- Propriedades Internas ---
    private interval?: NodeJS.Timeout;

    constructor() {
        makeObservable(this, {
            container: observable,
            setSelectedContainer: action,
            reset: action,
            checkOpenDoor: action,
            openDoorCommand: action,
            emergencyOpenDoor: action,
        });
    }

    /** Define o compartimento que está em uso no momento. */
    setSelectedContainer(container?: ContainerModel): void {
        this.container = container;
    }

    /** Limpa o estado da store e interrompe qualquer verificação em andamento. */
    reset(): void {
        this.container = undefined;
        this.setIntervalOff();
    }

    /**
     * Verifica se a porta do compartimento atual foi fechada.
     * Se fechada, limpa o estado e para a verificação.
     * @param test - Se true, exibe um toast com informações de debug.
     */
    async checkOpenDoor(test: boolean = false): Promise<void> {
        if (!this.container) return;

        try {
            const { result, resultData } = await LocalAPI.checkOpenDoor(
                this.container.serialName,
                this.container.serialPort!.name,
            );

            // Se o resultado for 'false', a porta foi fechada.
            if (!result) {
                this.reset();
            }

            if (test && this.container) {
                ToastHelper.success({
                    message: `Porta Fisica: ${this.container.name} - Porta Logica: ${this.container.serialName} \n Placa: ${this.container.serialPort!.name}\n\n` + resultData,
                    duration: 2000
                });
            }
        } catch (err) {
            console.error("Erro ao verificar status da porta:", err);
            this.reset(); // Reseta em caso de erro para evitar loops infinitos
        }
    }

    /**
     * Envia o comando para abrir a porta do compartimento selecionado.
     * @param test - Se true, o comando é enviado em modo de teste.
     * @returns `true` se o comando foi enviado com sucesso, `false` caso contrário.
     */
    async openDoorCommand(test: boolean = false): Promise<boolean> {
        if (!this.container) return false;

        try {
            await LocalAPI.openDoorCommand(
                this.container.serialName,
                this.container.serialPort!.name,
            );
            this.setIntervalOn(test);
            return true;
        } catch (err) {
            console.error("Erro ao enviar comando para abrir porta:", err);
            return false;
        }
    }

    /**
     * Envia um comando de abertura de emergência para uma porta específica.
     */
    async emergencyOpenDoor(serialPortName: string, serialName: string): Promise<void> {
        try {
            await LocalAPI.openDoorCommand(serialName, serialPortName);
        } catch (err) {
            console.error("Erro ao enviar comando de abertura de emergência:", err);
        }
    }

    // --- Métodos de Controle do Intervalo ---

    /** Ativa o intervalo de verificação do status da porta. */
    private setIntervalOn(test: boolean = false): void {
        this.setIntervalOff(); // Garante que não haja intervalos duplicados
        this.interval = setInterval(() => this.checkOpenDoor(test), 1000);
    }

    /** Desativa o intervalo de verificação. */
    private setIntervalOff(): void {
        if (this.interval) {
            clearInterval(this.interval);
        }
        this.interval = undefined;
    }
}

export default DoorsStore;